import type { ModoTramoUsdt } from '../../services/main-rates.service';
import { letraDe } from './franjas-editor.rules';
import { MARGEN_SOBRE_VENTA_PCT, SimulacionUsdt, TasaLocal, TOLERANCIA_MONOTONIA } from './tramos-usdt-editor.rules';

/**
 * Reglas puras de los tramos USDT→VES (21/10/2026): el ESPEJO de
 * `tramos-usdt-editor.rules.ts` (VES→USDT). Sin Angular ni HTTP.
 *
 * Espejo de `calcularTramosUsdtVes` / `techoTramosUsdtVes` de
 * `tramos-usdt.calculo.ts` del back. El cliente VENDE USDT y recibe
 * `monto × tasa` VES: una tasa más ALTA es mejor para él. Por eso:
 *
 *   - margen = (referencia − tasa) ÷ referencia (bajo la referencia);
 *   - TECHO en vez de piso: ningún tramo paga más que
 *     min(referencia × (1 − margen mínimo), mejor tasa de compra VES→USDT ÷ 1,005);
 *   - ningún tramo paga menos que la base;
 *   - máximo acumulado: un tramo más grande nunca paga menos que el anterior.
 *
 * El borrador, la estructura de los cortes, la barra y los textos del
 * historial son los mismos de VES→USDT: se reusan de `tramos-usdt-editor.rules`.
 */

/** Monto de ejemplo del simulador, en USDT (acá el cliente escribe los USDT). */
export const MONTO_SIM_USDT = 1_000;

export const floor2 = (x: number): number => Math.floor(x * 100 + 1e-9) / 100;

/** Margen % bajo la referencia (positivo = ganamos). */
export function margenBajo(tasa: number, referencia: number): number {
  return (1 - tasa / referencia) * 100;
}

/** Margen de la base bajo el costo, en %. */
export function margenBaseDe(referencia: number | null | undefined, base: number | null | undefined): number | null {
  const r = Number(referencia);
  const b = Number(base);
  if (!(r > 0) || !(b > 0)) return null;
  return margenBajo(b, r);
}

/** Cuánto margen se puede regalar entre el tramo chico y el candado. */
export function espacioDe(
  referencia: number | null | undefined,
  base: number | null | undefined,
  margenMinPct: number | null | undefined,
): number | null {
  const m = margenBaseDe(referencia, base);
  const min = Number(margenMinPct);
  if (m == null || margenMinPct == null || !Number.isFinite(min)) return null;
  return Math.max(0, m - min);
}

/**
 * Tasa máxima que ningún tramo puede pasar: el margen mínimo bajo el costo y,
 * si hay tasa de compra VES→USDT, ella ÷ 1,005 (vender USDT y comprarlo de
 * vuelta nunca le deja ganancia al cliente). Gana la más baja.
 */
export function techoDe(
  referencia: number | null | undefined,
  margenMinPct: number | null | undefined,
  mejorTasaCompra?: number | null,
): number | null {
  const r = Number(referencia);
  const min = Number(margenMinPct);
  if (!(r > 0) || margenMinPct == null || !Number.isFinite(min)) return null;
  const porMargen = floor2(r * (1 - min / 100));
  const c = Number(mejorTasaCompra);
  const porCompra =
    mejorTasaCompra != null && Number.isFinite(c) && c > 0
      ? floor2(c / (1 + MARGEN_SOBRE_VENTA_PCT / 100))
      : Infinity;
  return Math.min(porMargen, porCompra);
}

/**
 * Tasa de cada tramo activo (ya ordenados por «desde»). Vacío si faltan
 * insumos. Misma cuenta que `calcularTramosUsdtVes` del back.
 */
export function calcularTasas(
  referencia: number | null | undefined,
  base: number | null | undefined,
  margenMinPct: number | null | undefined,
  tramos: { modo: ModoTramoUsdt; ajuste: number | null }[],
  mejorTasaCompra?: number | null,
): TasaLocal[] {
  const ref = Number(referencia);
  const b = Number(base);
  const min = Number(margenMinPct);
  if (!(ref > 0) || !(b > 0) || margenMinPct == null || !Number.isFinite(min) || min < 0) return [];
  const n = tramos.length;
  if (!n) return [];

  const margenBase = margenBajo(b, ref);
  const espacio = Math.max(0, margenBase - min);
  const techo = techoDe(ref, min, mejorTasaCompra) as number;

  let anterior = -Infinity;
  return tramos.map((t, i) => {
    const pedido =
      t.modo === 'PCT' ? Number(t.ajuste) : n === 1 ? margenBase : margenBase - (espacio * i) / (n - 1);
    const calculada = i === 0 && t.modo === 'AUTO' ? b : floor2(ref * (1 - pedido / 100));
    const propia = b >= techo ? b : Math.max(b, Math.min(techo, calculada));
    // Máximo acumulado: un tramo más grande paga igual o más que el anterior.
    const tasa = Math.max(propia, anterior);
    anterior = tasa;
    return {
      tasa,
      tasaPropia: propia,
      calculada,
      margenPct: margenBajo(tasa, ref),
      mejoraPct: (tasa / b - 1) * 100,
      // Un centavo de diferencia es redondeo del techo, no un recorte.
      recortado: Math.abs(tasa - calculada) > 0.011,
    };
  });
}

/**
 * Letras de los tramos que, por sí solos, pagarían menos que el anterior. El
 * back bloquea guardar así (`tramosUsdtVesPeoresQueElAnterior`).
 */
export function tramosPeores(locales: TasaLocal[], letras: string[]): string[] {
  return locales
    .map((t, i) => ({ t, i }))
    .filter(({ t, i }) => i > 0 && t.tasaPropia < locales[i - 1].tasaPropia - TOLERANCIA_MONOTONIA)
    .map(({ i }) => letras[i] ?? letraDe(i));
}

/** Errores de tasa que bloquean guardar: a mayor monto, igual o mayor tasa. */
export function erroresMonotonia(locales: TasaLocal[], letras: string[]): string[] {
  return tramosPeores(locales, letras).map(
    (l) => `El tramo ${l} pagaría menos que el anterior: a mayor monto, igual o mayor tasa.`,
  );
}

/**
 * Qué tramo aplica a una venta de `montoUsdt`. Acá el monto ya está en USDT:
 * el tramo es el más alto cuyo «desde» no lo supera (igual que el back); bajo
 * el primero, el primero. `usdtBase` es el mismo monto.
 */
export function simular(tramos: { desde: number | null }[], montoUsdt: number | null | undefined): SimulacionUsdt {
  const m = Number(montoUsdt);
  if (montoUsdt == null || !(m > 0)) return { indice: -1, usdtBase: null, mensaje: 'Escribe un monto mayor que 0.' };
  if (!tramos.length) return { indice: -1, usdtBase: m, mensaje: 'Sin tramos: todos reciben la tasa base.' };
  let indice = 0;
  tramos.forEach((t, i) => {
    if (t.desde != null && m >= Number(t.desde)) indice = i;
  });
  return { indice, usdtBase: m, mensaje: null };
}

/** VES que recibe el cliente por `montoUsdt` con la tasa `tasa`. */
export function recibeVes(montoUsdt: number | null | undefined, tasa: number | null | undefined): number | null {
  const m = Number(montoUsdt);
  const t = Number(tasa);
  if (!(m > 0) || !(t > 0)) return null;
  return m * t;
}

function f2(v: number): string {
  return Number(v).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Aviso de tasa fija manual (MANUAL_FIJO) con la base y el techo. Vacío si falta la base. */
export function avisoManualFijo(base: number | null | undefined, techo: number | null | undefined): string {
  const b = Number(base);
  if (base == null || !(b > 0)) return '';
  const x = f2(b);
  const t = Number(techo);
  const hayTecho = techo != null && Number.isFinite(t) && t > 0;
  if (hayTecho && b >= t) {
    return `La tasa fija ${x} está en el techo (${f2(t)}) o encima: todos los clientes reciben ${x}, sin mejora por tramo.`;
  }
  return (
    `Tasa fija activa: el tramo chico pasa a ${x} VES/USDT para todos los clientes. ` +
    `Los tramos automáticos se reparten entre ${x} y ${hayTecho ? f2(t) : '—'}; ninguno paga menos que ${x}.`
  );
}
